import { Box, Typography, useTheme } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Legend, PolarAngleAxis, PolarGrid, Radar, RadarChart as RChart, ResponsiveContainer } from "recharts";
import muiSytle from "../../style/components-style/muiStyle";
import DownloadComponentButton from "../button/DownloadComponentButton";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";

const RadarChart = ({
  data = [
    { axis: "axis_1", item_1_name: 12, item_2_name: 22, item_3_name: 15 },
    { axis: "axis_2", item_1_name: 11, item_2_name: 60, item_3_name: 90 },
    { axis: "axis_3", item_1_name: 10, item_2_name: 80, item_3_name: 20 },
  ],
  colors = [GRAPH_PALETTE.lcaStages.D, GRAPH_PALETTE.lcaStages.A1, GRAPH_PALETTE.lcaStages.A2, GRAPH_PALETTE.lcaStages.B1],
  items_name = ["item_1_name", "item_2_name", "item_3_name"],
  axis_name = "axis",
  title = "title",
  showTitle = true,
  showDownload = true,
}) => {
  const { t } = useTranslation();
  const ref = useRef();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [visibility, setVisibility] = useState(false);
  const maxValues = useMemo(() => {
    let temp = {};
    data.forEach((d) => {
      items_name.forEach((n) => {
        if (temp[d[axis_name]]) {
          if (d[n] <= temp[d[axis_name]]) {
            return;
          }
        }
        temp[d[axis_name]] = d[n];
      });
    });
    return temp;
  }, [data, items_name]);
  const normalizedData = useMemo(() => {
    if (maxValues && Object.keys(maxValues).length === data.length && Object.keys(maxValues).every((v) => data.map((d) => d[axis_name]).includes(v))) {
      let temp = [];
      data.forEach((d) => {
        let t = { ...d };
        items_name.forEach((n) => {
          t[n] = maxValues[d[axis_name]] > 0 ? (t[n] / maxValues[d[axis_name]]) * 100 : 0;
        });
        temp.push(t);
      });
      return temp;
    }
    return data;
  }, [maxValues, data, items_name]);
  return (
    <Box onMouseEnter={() => setVisibility(true)} onMouseLeave={() => setVisibility(false)} sx={{ width: "100%", position: "relative" }}>
      {showDownload && (
        <Box sx={{ position: "absolute", top: "5px", right: "5px" }}>
          <DownloadComponentButton ref={ref} filename={t(title) + ".png"} visibility={visibility} />
        </Box>
      )}
      <Box ref={ref} sx={{ background: "transparent", p: 1 }}>
        {showTitle && title && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Typography variant="Chart Title">{t(title)}</Typography>
          </Box>
        )}
        <ResponsiveContainer width="100%" height={400}>
          <RChart cx="50%" cy="50%" outerRadius="70%" data={normalizedData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
            <PolarGrid strokeOpacity={isDark ? 0.3 : 0.1} />
            <PolarAngleAxis
              dataKey={axis_name}
              tickFormatter={(label) => t(label)}
              tick={{ fill: theme.palette.text.graphText || (isDark ? "#ffffff" : "#333333"), fontFamily: GRAPH_PALETTE.font.family, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 12, fontWeight: GRAPH_PALETTE.font.weights.standard }}
            />
            {items_name.map((n, i) => (
              <Radar
                key={i}
                name={t(n)}
                dataKey={n}
                stroke={colors[i % colors.length]}
                fill={colors[i % colors.length]}
                fillOpacity={0.4}
                strokeWidth={3}
              />
            ))}
            <Legend
              formatter={(value) => t(value)}
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              iconSize={18}
              wrapperStyle={{
                fontFamily: GRAPH_PALETTE.font.family,
                fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard) || 14,
                bottom: 0,
              }}
            />
          </RChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default RadarChart;
